import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { Box, Breadcrumbs, Link, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';

import { FilterPanelContainer, PageContainer } from 'components/GlobalStyles/styles';
import SideBarTabs from 'components/SideBarTabs';
import { JOB_MODAL, projectModalActions, TASK_MODAL } from 'features/Project/projectModalSlice';
import { projectFilterActions } from 'features/Project/projectSlice';
import ProjectManagementJob from 'pages/ProjectManagementJob';
import { useGetProjectByIdQuery, useGetProjectScopeQuery } from 'services/busmanApi';
import { PROJECT_SCOPE_STATUS } from 'utils/constants';
import { getStatusName } from 'utils/string';

import { RootState } from '../../store';

import ProjectBoardSideOptions from './component/ProjectBoardSideOptions';
import ProjectListSideOptions from './component/ProjectListSideOptions';
import ProjectManagementOverviewPage from './ProjectManagementOverviewPage';
import { SidebarContainer } from './styles';

const OVERVIEW_TAB = 0;
const JOBS_TAB = 1;
// const QUOTES_TAB = 2;

export const JOB_DRAWER_TAB = {
  LIST: 0,
  BOARD: 1,
  TIMELINE: 2,
};

/**
 * @url /project/{project_id}
 * @returns Project Management Overview
 */
const ProjectManagementOverview = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { projectID } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeParam = searchParams.get('scope');
  const { data: selectedProject } = useGetProjectByIdQuery(projectID);
  const [sideTabIndex, setSideTabIndex] = useState(OVERVIEW_TAB);
  const { data: fetchProjectScopeList } = useGetProjectScopeQuery({ projectId: projectID });

  const { projectScopeList, selectedProjectScope } = useSelector((state: RootState) => state.projectFilterReducer);

  const [sideJobTabIndex, setSideJobTabIndex] = useState(JOB_DRAWER_TAB.LIST);

  React.useEffect(() => {
    if (!fetchProjectScopeList) return;

    dispatch(projectFilterActions.setProjectScope(fetchProjectScopeList));

    // The URL (?scope=) is the source of truth so the selection survives a refresh.
    // Fall back to the project's current scope, then the first scope.
    const urlScopeValid = fetchProjectScopeList.some((scope) => scope.id === scopeParam);
    const currentScope = fetchProjectScopeList.find((scope) => scope.id === selectedProject?.current_scope?.id);
    const resolvedScope = urlScopeValid ? scopeParam : currentScope?.id ?? fetchProjectScopeList[0]?.id ?? null;

    dispatch(projectFilterActions.setSelectedProjectScope(resolvedScope));

    if (resolvedScope && resolvedScope !== scopeParam) {
      const next = new URLSearchParams(searchParams);
      next.set('scope', resolvedScope);
      setSearchParams(next, { replace: true });
    }
  }, [dispatch, fetchProjectScopeList, selectedProject?.current_scope?.id, scopeParam, searchParams, setSearchParams]);

  const handleScopeChange = (scopeId: string) => {
    dispatch(projectFilterActions.setSelectedProjectScope(scopeId));
    const next = new URLSearchParams(searchParams);
    next.set('scope', scopeId);
    setSearchParams(next, { replace: true });
  };

  const sidePanel = () => {
    return (
      <FilterPanelContainer sx={{ width: '16vw' }}>
        <Stack direction="column" justifyContent="space-between" gap={2}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 14 }}>
            <Link underline="hover" href="/project">
              Project
            </Link>
            <Link underline="none">{selectedProject?.name}</Link>
          </Breadcrumbs>

          <Stack direction="column" justifyContent="center" sx={{ gap: 1 }}>
            <Typography variant="h4">Selected Scope: </Typography>
            <Select
              size="small"
              value={selectedProjectScope ?? ''}
              data-testid="job-select-type"
              onChange={(e) => handleScopeChange(e.target.value)}>
              {Object.entries(projectScopeList).map(([key, value]) => (
                <MenuItem key={key} value={value.id}>
                  {value.version} - {getStatusName(PROJECT_SCOPE_STATUS, value.status)}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box sx={{ paddingY: 2 }}>
            <SideBarTabs
              tabs={['Overview / Scopes', 'Jobs', 'Quotes']}
              selectedTab={sideTabIndex}
              selectTab={setSideTabIndex}
            />
          </Box>

          {sideTabIndex === JOBS_TAB && (
            <SidebarContainer>
              <SideBarTabs tabs={['List', 'Board']} selectedTab={sideJobTabIndex} selectTab={setSideJobTabIndex} />

              {sideJobTabIndex === JOB_DRAWER_TAB.LIST && (
                <ProjectListSideOptions
                  showModal={() => dispatch(projectModalActions.openAddModal({ type: JOB_MODAL }))}
                />
              )}
              {sideJobTabIndex === JOB_DRAWER_TAB.BOARD && (
                <ProjectBoardSideOptions
                  showModal={() =>
                    dispatch(
                      projectModalActions.openAddModal({
                        type: TASK_MODAL,
                        data: {
                          job_id: null,
                          introduced_in_scope_id: null,
                        },
                      }),
                    )
                  }
                />
              )}
            </SidebarContainer>
          )}
        </Box>
      </FilterPanelContainer>
    );
  };

  return (
    <>
      <PageContainer>
        {sidePanel()}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            gap: theme.spacing(1),
            flexDirection: { xs: 'column', md: 'row' },
          }}>
          {sideTabIndex === OVERVIEW_TAB && selectedProject != null && (
            <ProjectManagementOverviewPage projectDetails={selectedProject} />
          )}

          {sideTabIndex === JOBS_TAB && <ProjectManagementJob sideJobTabIndex={sideJobTabIndex} />}
        </Box>
      </PageContainer>
    </>
  );
};

export default ProjectManagementOverview;
